import { useState, ChangeEvent, useEffect, KeyboardEvent } from 'react';
import { Modal, Form, Button as BootstrapButton } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import {
  HVAServicesTable,
  hvaservicessTablecolumns
} from 'components/tables/HVAServicesTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import {
  fetchHVAServices,
  HVAService,
  createHVAService
} from 'services/hvaservicecampaign.service';
import Button from 'components/base/Button';

const ServiceList = () => {
  const [hvaservices, setHVAServices] = useState<HVAService[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    description: ''
  });

  const loadServices = async () => {
    try {
      const hvaservicesData = await fetchHVAServices();
      setHVAServices(hvaservicesData);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  const table = useAdvanceTable({
    data: hvaservices,
    columns: hvaservicessTablecolumns(loadServices),
    pageSize: 5,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      description: ''
    });
  };

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createHVAService(formData);
      const updatedServices = await fetchHVAServices();
      setHVAServices(updatedServices);
      handleCloseModal();
    } catch (error) {
      alert('Error creating new service');
      handleCloseModal();
    }
  };

  return (
    <div>
      <div className="mb-4">
        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Search Services and Campaigns"
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyPress}
              />
              <div className="ms-auto order-2">
                <Button
                  variant="primary"
                  className="p-2"
                  onClick={handleShowModal}
                >
                  <FontAwesomeIcon icon={faFolderPlus} className="me-2 fs-8" />
                  Create New Service
                </Button>
              </div>
            </div>
          </div>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-translucent position-relative top-1">
            <HVAServicesTable />
          </div>
        </AdvanceTableProvider>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Create New Service</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Brand</Form.Label>
              <Form.Control
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <BootstrapButton variant="primary" type="submit">
              Create Service
            </BootstrapButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceList;
